/* eslint react-hooks/exhaustive-deps: "error" */
import React, { useEffect } from 'react';
import { useQuery } from '@apollo/client';
import InfiniteScroll from 'react-infinite-scroller';
// import _throttle from 'lodash/throttle';

import {
  Row,
  Col,
  PrimaryButton,
  Loader,
  LazyImage,
  colors,
} from '@crehana/ui';

// import ArrowTop from 'crehana-icons/ArrowTop';
import { useCrehanaLiveTranslations } from 'Jsx/CrehanaLive/hooks';

import { LiveCategory } from 'Jsx/CrehanaLive/types';
import GET_LIVE_SUBCATEGORIES from 'Jsx/CrehanaLive/graphql/GetLiveSubcategories.graphql';
import {
  GetLiveSubcategories,
  GetLiveSubcategoriesVariables,
} from 'Jsx/CrehanaLive/graphql/types/GetLiveSubcategories';
import { KIDS_SLUG } from 'Jsx/CrehanaLive/constants';
import useLivesQuery from 'Jsx/CrehanaLive/hooks/useLivesQuery';
import LiveCard, { CardType } from 'Jsx/CrehanaLive/components/LiveCard';
import CardLeadPhone from 'Jsx/CrehanaLive/components/CardLeadPhone';
import { useMe } from 'Jsx/ContextApis/AuthContext';
import getCountryPrefix from 'Jsx/Utils/getCountryPrefix';

const countryPrefix = getCountryPrefix();

interface LiveListProps {
  categorySelected?: LiveCategory;
  setCategorySelected?: (category?: LiveCategory) => void;
  isB2B?: boolean;
  customTitle?: string | null;
}

const LiveList: React.FC<LiveListProps> = ({
  setCategorySelected,
  categorySelected,
  isB2B,
  customTitle,
}) => {
  const { t } = useCrehanaLiveTranslations();
  const [subCategory, setSubCategory] = React.useState<string>();
  const LIVE_COUNT_PAGINATION = 12;
  const categories = categorySelected?.slug ? categorySelected.slug : '';
  const { lives, pageInfo, loading, error, refetch, fetchMore } = useLivesQuery(
    {
      first: LIVE_COUNT_PAGINATION,
      type: 'old',
      categories,
      subCategories: subCategory,
    },
    { isB2B: !!isB2B },
  );
  const { data: subCategoriesData } = useQuery<
    GetLiveSubcategories,
    GetLiveSubcategoriesVariables
  >(GET_LIVE_SUBCATEGORIES, { variables: { categories } });
  const { me: user, loading: loadingUser } = useMe();
  const isUserPremium =
    user &&
    (user.isPremiumMember ||
      user.hasOrganization ||
      user.isSofeMembership ||
      user.isStaff);
  const [showFalseCardLead, setShowFalseCardLead] = React.useState(
    !isUserPremium && countryPrefix !== 'br',
  );

  useEffect(() => {
    if (!loadingUser) {
      setShowFalseCardLead(!isUserPremium && countryPrefix !== 'br');
    }
  }, [loadingUser, isUserPremium]);

  const livesCount = lives.length;
  const isKidsSelected = categorySelected?.slug === KIDS_SLUG;

  const subCategories = React.useMemo(() => {
    const allCategories =
      subCategoriesData?.lives?.edges.map(e => e!.node!) || [];

    if (categorySelected && allCategories.length) {
      const [first] = allCategories;

      return (
        first?.liveCategory?.livesubcategorySet?.edges.map(e => e!.node!) || []
      );
    }
    return [];
  }, [categorySelected, subCategoriesData]);

  // if the category changes reset the sub category
  React.useEffect(() => setSubCategory(undefined), [categorySelected]);

  const liveListRef = React.useRef<HTMLDivElement>(null);

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center mt-32">
        <span className="font-body3 text-white">{t('ERROR_LIVE_LIST')}</span>
        <PrimaryButton
          className="inline-block mt-16"
          label={t('RETRY_AGAIN_LABEL')}
          onClick={() => refetch()}
          darkMode
        />
      </div>
    );
  }

  const customLoadMore = () =>
    fetchMore({
      variables: {
        after: pageInfo?.endCursor,
        categories,
        type: 'old',
        first: LIVE_COUNT_PAGINATION,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.lives) {
          return prev;
        }
        const { edges: prevEdges, __typename } = prev.lives || {};

        return {
          ...prev,
          lives: {
            pageInfo: {
              ...fetchMoreResult.lives.pageInfo,
            },
            edges: [...(prevEdges || []), ...fetchMoreResult.lives.edges],
            __typename: __typename!,
          },
        };
      },
    });
  let cardType: CardType = 'normal';
  let title: React.ReactNode = customTitle || t('ENJOY_TRANSMISSION');

  if (categorySelected) {
    title = (
      <>
        Transmisiones sobre{' '}
        <span className="text-secondary-light">{categorySelected.name}</span>
      </>
    );
  }
  if (isKidsSelected) {
    cardType = 'kids';
    title = t('DISCOVER_LIVE_KIDS');
  }
  if (isB2B) {
    cardType = 'b2b';
  }

  return (
    <div className="w-full">
      <h2 className="text-center text-white font-h3">{title}</h2>
      {isKidsSelected && subCategories.length ? (
        <Row className="mt-24">
          {subCategories.map(
            ({
              originalId,
              coverImage,
              slug,
              name,
              buttonBackgroundColor,
              buttonTextColor,
            }) => {
              const isSomethingSelected = subCategory !== undefined;
              const isSelected = slug === subCategory;

              return (
                <Col key={originalId!} xs={12} sm={3} md={3}>
                  <button
                    type="button"
                    className="relative flex items-center w-full outline-none"
                    style={{
                      backgroundColor:
                        buttonBackgroundColor || colors.purpleLight,
                    }}
                    onClick={() =>
                      setSubCategory(prev =>
                        prev !== slug ? slug! : undefined,
                      )
                    }
                  >
                    {coverImage && (
                      <LazyImage
                        className="flex-shrink-0 h-64 w-72 sm:h-80 sm:w-80"
                        imgClassName="object-cover w-72"
                        src={coverImage}
                        alt={name || ''}
                        isDark
                      />
                    )}
                    <div className="flex w-full px-24 text-left sm:justify-center sm:px-4 md:px-20">
                      <p
                        className="font-button break-words"
                        style={{ color: buttonTextColor || colors.white }}
                      >
                        {name}
                      </p>
                    </div>
                    {isSomethingSelected && !isSelected ? (
                      <div className="absolute inset-0 bg-base-main-80" />
                    ) : null}
                  </button>
                </Col>
              );
            },
          )}
        </Row>
      ) : null}
      {loading ? (
        <div className="flex items-center justify-center pt-32 pb-56">
          <Loader isWhite />
        </div>
      ) : (
        <div className="relative mt-32" ref={liveListRef}>
          {livesCount > 0 ? (
            <InfiniteScroll
              loadMore={customLoadMore}
              hasMore={pageInfo?.hasNextPage}
              loader={
                <div
                  key="live-list-222loader"
                  className="flex justify-center w-full pt-32 pb-56"
                >
                  <Loader isWhite />
                </div>
              }
              initialLoad={false}
              useWindow
            >
              <Row className="w-full">
                {lives.map((live, index) => (
                  <Col
                    key={live.originalId!}
                    className="inline-block mb-24"
                    xs={12}
                    sm={4}
                    md={3}
                  >
                    {index === 3 && showFalseCardLead ? (
                      <CardLeadPhone
                        hidenCardDialog={() => setShowFalseCardLead(false)}
                      />
                    ) : (
                      <LiveCard
                        type={cardType}
                        onClickCategory={category =>
                          setCategorySelected && setCategorySelected(category)
                        }
                        {...live}
                      />
                    )}
                  </Col>
                ))}
              </Row>
            </InfiniteScroll>
          ) : (
            <div className="w-full pt-40 text-center">
              <span className="font-subtitle2 text-white">
                {t('EMPTY_CONTENT_LIVE_LIST')}
              </span>
              <br />
              <PrimaryButton
                className="mt-24"
                label={t('BACK_TOP_LIST')}
                onClick={() =>
                  setCategorySelected && setCategorySelected(undefined)
                }
                darkMode
              />
            </div>
          )}
          {/* {showBackToTopButton && (
          <button
            type="button"
            className="fixed flex items-center justify-center w-32 h-32 rounded-full bg-primary-main"
            css={`
              right: 20px;
                bottom: 120px;
                width: 50px;
                height: 50px;
              ${props => props.theme.breakpoints.up('md')} {
                right: 20px;
                bottom: 120px;
                width: 50px;
                height: 50px;
              }
            `}
            onClick={backToTop}
          >
            <span className="block md:hidden">
              <ArrowTop size={12} color="#fff" />
            </span>
            <span className="hidden md:block">
              <ArrowTop size={16} color="#fff" />
            </span>
          </button>
        )} */}
        </div>
      )}
    </div>
  );
};

export default LiveList;
